import { useEffect, useRef, type ReactNode } from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";

export type Notify = (message: string, error?: boolean) => void;
export const laneColor = (lane: string) =>
  ({
    backlog: "gray",
    ready: "blue",
    in_progress: "amber",
    wait_user: "purple",
    done: "green",
  })[lane] ?? "blue";
export const kinds = ["decision", "gotcha", "failed", "file", "text"] as const;
export function IconButton({
  label,
  onClick,
  children,
  ...props
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      className={`icon-button ${props.className ?? ""}`}
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={props.disabled}
    >
      {children}
    </button>
  );
}
export function Badge({
  children,
  tone = "gray",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`badge ${tone}`}>{children}</span>;
}
export function Priority({ value }: { value: string }) {
  return (
    <span className={`priority ${value}`} title={`${value} priority`}>
      {value === "urgent" ? (
        <>
          <ArrowUp size={12} />
          <ArrowUp size={12} />
        </>
      ) : value === "high" ? (
        <ArrowUp size={14} />
      ) : value === "low" ? (
        <ArrowDown size={14} />
      ) : (
        <span>—</span>
      )}
    </span>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement;
    // Capture the opener before applying initial focus. Child autoFocus runs
    // earlier and would make cleanup try to restore focus to a removed input.
    const initial =
      ref.current?.querySelector<HTMLElement>("[data-initial-focus]") ??
      ref.current?.querySelector<HTMLElement>("input,textarea,button");
    initial?.focus();
    const onKey = (e: KeyboardEvent) => {
      // A native attachment dialog owns focus and keyboard input while open.
      if (document.querySelector("dialog[open]")) return;
      if (e.key === "Escape") close.current();
      if (e.key === "Tab") {
        const elements = [
          ...ref.current!.querySelectorAll<HTMLElement>(
            "button:not(:disabled),input,textarea,select,a[href]",
          ),
        ].filter((el) => el.offsetParent !== null);
        const first = elements[0],
          last = elements.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = old;
      prev?.focus();
    };
    // Focus setup/cleanup belongs to the dialog lifetime, never a new callback
    // from a parent render (including SSE updates and fallback polling).
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal ${wide ? "wide" : ""}`}
      >
        <div className="modal-heading">
          <h2>{title}</h2>
          <IconButton label="Close dialog" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>
        {children}
      </div>
    </div>
  );
}
